"use client";

import { useCallback } from "react";
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  type DragEndEvent,
} from "@dnd-kit/core";
import {
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
  arrayMove,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { restrictToVerticalAxis } from "@dnd-kit/modifiers";
import {
  Sheet,
  SheetHeader,
  SheetTitle,
  SheetClose,
  SheetBody,
} from "@/components/ui/sheet";
import { Icon, type IconName } from "@/components/ui/icon";
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuCheckboxItem,
} from "@/components/ui/dropdown-menu";
import {
  usePipelineTableStore,
  FIXED_COLUMNS,
} from "@/features/jobs/store/pipeline/table";
import type { ColumnConfig } from "@/components/ui/ar-table";

const FIELD_ICONS: Record<string, IconName> = {
  email: "IconEmail1",
  socials: "IconWorld",
  stage: "IconPlan",
  sync: "IconArrowsRepeatRightLeft",
  sequences: "IconPaperPlane",
  job_title: "IconMedal",
  company: "IconBuildings",
  location: "IconMapPin",
  education: "IconGraduateCap",
  added_at: "IconPlusSmall",
};

interface Props {
  open: boolean;
  onClose: () => void;
  /**
   * Column ids force-hidden at the table level (e.g. "sync" when the job has
   * no ATS). Their toggles are dropped from this panel so users aren't offered
   * a switch that does nothing.
   */
  hiddenColumns?: string[];
}

export function PipelineViewOptions({ open, onClose, hiddenColumns }: Props) {
  const { columns, toggleColumn, reorderColumns, resetColumns } =
    usePipelineTableStore();

  const hidden = new Set(hiddenColumns ?? []);
  const toggleable = columns
    .filter((c) => !FIXED_COLUMNS.has(c.id) && !hidden.has(c.id))
    .sort((a, b) => a.position - b.position);
  const visibleToggleable = toggleable.filter((c) => c.visible);
  const selectedCount = visibleToggleable.length + FIXED_COLUMNS.size;

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 4 } }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    }),
  );

  const handleDragEnd = useCallback(
    (event: DragEndEvent) => {
      const { active, over } = event;
      if (!over || active.id === over.id) return;

      const sorted = [...columns].sort((a, b) => a.position - b.position);
      const oldIndex = sorted.findIndex((c) => c.id === active.id);
      const newIndex = sorted.findIndex((c) => c.id === over.id);
      if (oldIndex < 0 || newIndex < 0) return;

      const reordered = arrayMove(sorted, oldIndex, newIndex).map((c, i) => ({
        ...c,
        position: i,
      }));
      reorderColumns(reordered);
    },
    [columns, reorderColumns],
  );

  return (
    <Sheet
      open={open}
      onOpenChange={(v) => {
        if (!v) onClose();
      }}
      size="sm"
    >
      <SheetHeader>
        <SheetTitle>View Options</SheetTitle>
        <SheetClose />
      </SheetHeader>
      <SheetBody>
        <div className="flex flex-col gap-5">
          {/* Layout */}
          <div className="flex flex-col gap-2">
            <div>
              <p className="text-sm font-medium text-heading">Layout</p>
              <p className="text-sm text-contrast/60">
                Select the visual format for this view.
              </p>
            </div>
            <div className="flex flex-col rounded-lg overflow-hidden border border-border">
              <div className="flex items-center justify-center h-11 bg-contrast/[0.02]">
                <Icon
                  name="IconBulletList"
                  size={23}
                  className="text-contrast/70"
                />
              </div>
              <div className="flex items-center justify-between px-2 py-2 bg-contrast/8">
                <span className="text-sm font-medium text-heading pl-1">
                  Table
                </span>
                <Icon
                  name="IconCircleCheck"
                  fill="filled"
                  size={16}
                  className="text-heading"
                />
              </div>
            </div>
          </div>

          {/* Fields */}
          <div className="flex flex-col gap-2.5">
            <div>
              <p className="text-sm font-medium text-heading">Fields</p>
              <p className="text-sm text-contrast/60">
                Select and reorder columns.
              </p>
            </div>

            {/* Column visibility — Radix DropdownMenu */}
            <DropdownMenu modal={false}>
              <DropdownMenuTrigger asChild>
                <button
                  type="button"
                  className="flex items-center w-full h-9 rounded-lg border border-border bg-contrast/6 px-2 gap-1.5 cursor-pointer shadow-[0px_1px_2px_0px_rgba(0,0,0,0.05),inset_0px_-1px_0px_0px_rgba(0,0,0,0.08)]"
                >
                  <Icon
                    name="IconBulletList"
                    size={16}
                    className="text-contrast/60 shrink-0"
                  />
                  <span className="text-sm text-contrast/80 flex-1 text-left">
                    {selectedCount} Fields Selected
                  </span>
                  <Icon
                    name="IconChevronGrabberVertical"
                    size={16}
                    className="text-contrast/50 shrink-0"
                  />
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent
                align="start"
                className="min-w-[var(--radix-dropdown-menu-trigger-width)]"
              >
                {toggleable.map((col) => (
                  <DropdownMenuCheckboxItem
                    key={col.id}
                    checked={col.visible}
                    onCheckedChange={() => toggleColumn(col.id)}
                    onSelect={(e) => e.preventDefault()}
                  >
                    <Icon
                      name={FIELD_ICONS[col.id] ?? "IconBulletList"}
                      size={14}
                      className="text-contrast/50 shrink-0"
                    />
                    {col.label}
                  </DropdownMenuCheckboxItem>
                ))}
              </DropdownMenuContent>
            </DropdownMenu>

            {/* Reorderable visible columns */}
            {visibleToggleable.length > 0 && (
              <DndContext
                sensors={sensors}
                collisionDetection={closestCenter}
                modifiers={[restrictToVerticalAxis]}
                onDragEnd={handleDragEnd}
              >
                <SortableContext
                  items={visibleToggleable.map((c) => c.id)}
                  strategy={verticalListSortingStrategy}
                >
                  <div className="flex flex-col gap-1">
                    {visibleToggleable.map((col) => (
                      <SortableFieldRow
                        key={col.id}
                        col={col}
                        onRemove={() => toggleColumn(col.id)}
                      />
                    ))}
                  </div>
                </SortableContext>
              </DndContext>
            )}

            {/* Reset */}
            <button
              type="button"
              onClick={resetColumns}
              className="text-xs text-contrast/40 hover:text-contrast/60 transition-colors cursor-pointer self-start"
            >
              Reset to defaults
            </button>
          </div>
        </div>
      </SheetBody>
    </Sheet>
  );
}

function SortableFieldRow({
  col,
  onRemove,
}: {
  col: ColumnConfig;
  onRemove: () => void;
}) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({
    id: col.id,
  });

  const style: React.CSSProperties = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      className="flex items-center gap-2 rounded-lg bg-contrast/4 border border-contrast/6 px-2.5 h-9 select-none"
    >
      <div
        {...attributes}
        {...listeners}
        className="cursor-grab active:cursor-grabbing shrink-0"
      >
        <Icon name="IconDotGrid3x3" size={14} className="text-contrast/30" />
      </div>
      <Icon
        name={FIELD_ICONS[col.id] ?? "IconBulletList"}
        size={14}
        className="text-contrast/50 shrink-0"
      />
      <span className="text-sm text-contrast/80 flex-1">{col.label}</span>
      <button
        type="button"
        onClick={(e) => {
          e.stopPropagation();
          onRemove();
        }}
        className="shrink-0 cursor-pointer hover:text-contrast/60 transition-colors"
      >
        <Icon name="IconCrossMedium" size={12} className="text-contrast/30" />
      </button>
    </div>
  );
}
